import { FC, SVGProps, useCallback, useEffect, useState } from 'react';
import { useGetPolicyTemplates, useGetPolicyTemplateCategories } from '../api';
import TemplatesListLoader from './components/TemplatesListLoader';
import CategoriesHeader from './components/CategoriesHeader';
import SearchTemplates from './SearchTemplates';
import Menu, { MenuOption } from './components/Menu';
import TemplatesRoutes from './TemplatesRoutes';
import { Button, ButtonSizes, ButtonStyles, EmptyState, EmptyStateIcons, Icon, Icons } from '@silverfort/sf-components';
import { PolicyTemplatesContext, usePolicyTemplatesContextValue } from './context/PolicyTemplatesContext';
import { useTowerLayout } from '../../../TowerLayout/TowerLayoutProvider';
import { usePolicyTemplatesGoBackUntilRoute } from './hooks/usePolicyTemplatesGoBackUntilRoute';
import { useHistory } from 'react-router-dom';
import { usePolicyConfigurationContext } from './context/PolicyConfigurationContext';
import { PolicyTemplateCategory } from './types/category';
import { PolicyTemplateStaticCategory } from '../../../types/policies';

const STATIC_CATEGORIES: PolicyTemplateCategory[] = [
    {
        id: PolicyTemplateStaticCategory.FAVORITES,
        name: 'Favorites'
    },
    {
        id: PolicyTemplateStaticCategory.CUSTOM,
        name: 'Custom'
    }
];

interface PolicyTemplatesLayoutProps {
    categoryId: string;
}

interface ContentProps {
    loading: boolean;
    selectedCategory: string;
    error?: unknown;
}

type TemplatesMenuOption = MenuOption<string>;

const compareCategories = (categoryA: PolicyTemplateCategory, categoryB: PolicyTemplateCategory): number => {
    const STATIC_CATEGORY_IDS: string[] = Object.values(PolicyTemplateStaticCategory);

    const isStaticCategoryA = STATIC_CATEGORY_IDS.includes(categoryA.id);
    const isStaticCategoryB = STATIC_CATEGORY_IDS.includes(categoryB.id);

    if (isStaticCategoryA && !isStaticCategoryB) {
        return 1;
    }

    if (!isStaticCategoryA && isStaticCategoryB) {
        return -1;
    }

    return categoryA.name.localeCompare(categoryB.name);
};

const getMenuOptions = (categories: PolicyTemplateCategory[]): TemplatesMenuOption[] => {
    return categories.sort(compareCategories).map((templateType) => ({
        id: templateType.id,
        label: templateType.name,
        backgroundColor: templateType.backgroundColor
    }));
};

const Content: FC<ContentProps> = ({ loading, selectedCategory, error }) => {
    if (loading) {
        return <TemplatesListLoader />;
    }

    if (error) {
        return (
            <div className="rounded-[8px] my-10 mx-10 border border-sf-gray-100 flex flex-col items-center">
                <EmptyState
                    icon={EmptyStateIcons.Error as FC<SVGProps<SVGSVGElement>>}
                    title="Error"
                    contentClassName="whitespace-pre-line"
                    description={`An error occurred loading the Policy Template library. Try again.\nIf the problem persists, contact your Silverfort representative.`}
                />
            </div>
        );
    }

    return <TemplatesRoutes selectedCategory={selectedCategory} />;
};

const PolicyTemplatesLayout: FC<PolicyTemplatesLayoutProps> = ({ categoryId }) => {
    const history = useHistory();
    const { data: categories = [] } = useGetPolicyTemplateCategories();
    const { setLayoutContent, setLayoutHeader, setLayoutContentIcon } = useTowerLayout();
    const menuOptions = getMenuOptions([...STATIC_CATEGORIES, ...categories]);
    const [menuOpen, setMenuOpen] = useState(true);
    const { templates, loading: loadingPolicyTemplates, error: policyTemplatesError } = useGetPolicyTemplates();
    const policyTemplatesContextValue = usePolicyTemplatesContextValue(templates ?? []);
    const { loading: loadingPolicyConfig, error: policyConfigError } = usePolicyConfigurationContext();
    const { goBackUntilRoute } = usePolicyTemplatesGoBackUntilRoute();

    const loading = loadingPolicyTemplates || loadingPolicyConfig;
    const error = policyTemplatesError ?? policyConfigError;

    const handleCancelClicked = useCallback(() => {
        goBackUntilRoute('/Policy');
    }, [goBackUntilRoute]);

    useEffect(() => {
        setLayoutContentIcon(
            <Icon className="text-sf-gray-400" icon={Icons.Blocks as FC<SVGProps<SVGSVGElement>>} size={20} />
        );
        setLayoutHeader('Policy Templates');
        setLayoutContent(
            <Button
                buttonStyle={ButtonStyles.GhostSolid}
                buttonSize={ButtonSizes.Small}
                iconRight={Icons.Cancel as FC<SVGProps<SVGSVGElement>>}
                onClick={handleCancelClicked}
            />
        );

        return () => {
            setLayoutContentIcon(null);
            setLayoutHeader('');
            setLayoutContent(null);
        };
    }, [handleCancelClicked, setLayoutHeader, setLayoutContentIcon, setLayoutContent]);

    const handleToggle = () => {
        setMenuOpen((prevState) => !prevState);
    };

    const navigateToCategory = (categoryId: string) => {
        history.replace(`/Policy/Templates/${categoryId}`);
    };

    return (
        <div id="wrapper" className="h-screen bg-sf-white flex flex-col">
            <div className="flex flex-col flex-1 overflow-hidden">
                <PolicyTemplatesContext.Provider value={policyTemplatesContextValue}>
                    <div className="h-12 flex border-b border-sf-gray-100">
                        <CategoriesHeader collapsed={!menuOpen} onToggle={handleToggle} />
                        <SearchTemplates />
                    </div>
                    <div className="flex flex-1 overflow-hidden">
                        <div
                            id="menu-container"
                            className={`h-full overflow-hidden transition-[width] ease-in duration-[225ms] ${
                                menuOpen ? 'w-50' : 'w-0'
                            }`}>
                            <Menu
                                options={menuOptions}
                                selectedOptionId={categoryId}
                                onOptionClick={navigateToCategory}
                            />
                        </div>

                        <div className="flex-1">
                            <Content loading={loading} error={error} selectedCategory={categoryId} />
                        </div>
                    </div>
                </PolicyTemplatesContext.Provider>
            </div>
        </div>
    );
};

export default PolicyTemplatesLayout;
